import { ColumnDef } from './types';

/**
 * Filter interface for column filtering
 */
export interface ColumnFilter<_TRow = unknown, _TValue = unknown> {
  /** Column ID this filter applies to */
  columnId: string;

  /** Filter values/state */
  filterState: unknown;
}

/**
 * Filter implementation that knows how to filter data
 */
export interface FilterImpl<TRow = unknown, TValue = unknown> {
  /** Check if a row passes the filter */
  matches(value: TValue, filterState: unknown): boolean;

  /** Get all unique values from the dataset for this column */
  getUniqueValues(data: TRow[], column: ColumnDef<TRow, TValue>): TValue[];

  /** Get display label for a value */
  getLabel(value: TValue, column: ColumnDef<TRow, TValue>): string;
}

/**
 * Discrete values filter - for columns with a finite set of possible values
 * Shows a multi-select dropdown with all unique values
 */
export class DiscreteValuesFilter<TRow = unknown, TValue = unknown> implements FilterImpl<TRow, TValue> {
  matches(value: TValue, filterState: Set<TValue>): boolean {
    // If no filter selected, show all
    if (!filterState || filterState.size === 0) {
      return true;
    }
    return filterState.has(value);
  }

  getUniqueValues(data: TRow[], column: ColumnDef<TRow, TValue>): TValue[] {
    const uniqueSet = new Set<TValue>();
    data.forEach(row => {
      const value = column.accessor(row);
      if (value != null) {
        uniqueSet.add(value);
      }
    });

    const values = Array.from(uniqueSet);

    // Sort the values using dataType.compare if available
    if (column.dataType) {
      values.sort((a, b) => column.dataType!.compare(a, b));
    } else {
      values.sort();
    }

    return values;
  }

  getLabel(value: TValue, column: ColumnDef<TRow, TValue>): string {
    if (column.dataType?.getFilterLabel) {
      return column.dataType.getFilterLabel(value);
    }
    if (column.dataType?.render) {
      const rendered = column.dataType.render(value);
      return typeof rendered === 'string' ? rendered : String(value);
    }
    return String(value);
  }
}

/**
 * Helper to create a discrete values filter state
 */
export function createDiscreteFilter<TValue = unknown>(
  columnId: string,
  selectedValues: Set<TValue> = new Set()
): ColumnFilter<unknown, TValue> {
  return {
    columnId,
    filterState: selectedValues,
  };
}

/**
 * Parent relation filter - filters child rows based on parent row selection
 * Example: Show orders for selected customer, show line items for selected order
 */
export class ParentRelationFilter<TChild = unknown, TParentKey = unknown> implements FilterImpl<TChild, TParentKey> {
  matches(childValue: TParentKey, filterState: TParentKey | undefined): boolean {
    // If no parent selected, show nothing (or optionally show all)
    if (filterState === undefined || filterState === null) {
      return false; // Don't show any children when no parent selected
    }
    // Child matches if its parent key equals the selected parent key
    return childValue === filterState;
  }

  getUniqueValues(_data: TChild[], _column: ColumnDef<TChild, TParentKey>): TParentKey[] {
    // Parent relation filter doesn't need unique values UI
    return [];
  }

  getLabel(value: TParentKey, _column: ColumnDef<TChild, TParentKey>): string {
    return String(value);
  }
}
